import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { submitPartner } from "@/lib/api";

const BG =
  "https://customer-assets.emergentagent.com/job_gower-golf-portal/artifacts/52cr0i65_ChatGPT%20Image%20May%2029%2C%202026%2C%2010_27_03%20PM.png";

export default function Partners() {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((p) => ({
      ...p,
      [k]: e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.company_name ||
      !form.contact_name ||
      !form.email ||
      !form.message
    ) {
      toast.error("Please complete all partnership fields.");
      return;
    }

    setLoading(true);

    try {
      await submitPartner(form);

      toast.success(
        "Partnership inquiry received. Our commercial team will respond shortly."
      );

      setForm({
        company_name: "",
        contact_name: "",
        email: "",
        message: "",
      });
    } catch (err) {
      toast.error("Unable to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="partner"
      data-testid="partner-section"
      className="bg-white py-24 md:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* SECTION HEADER */}
        <div className="max-w-5xl mb-16 md:mb-20">
          <span className="section-eyebrow">
            05 · Partner With Us
          </span>

          <h2 className="font-serif-luxury text-4xl md:text-5xl lg:text-6xl text-[#0F4C3A] mt-5 leading-[1.05] tracking-tight">
            Corporate Sponsorship in
            <span className="block italic text-gold-metallic">
              Swansea &amp; South Wales
            </span>
          </h2>

          <p className="text-[#4A4A4A] mt-6 max-w-2xl leading-relaxed">
            Partner with The Gower Golf Club and connect your brand with
            members, guests and visiting golfers from across the UK.
          </p>
        </div>

        {/* IMAGE + FORM */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">

          {/* IMAGE */}
          <div className="lg:col-span-6 relative image-frame border border-[#CBA153]/30 min-h-[520px]">
            <img
              src={BG}
              alt="The Gower Golf Club golf course"
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Green overlay */}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0F4C3A]/85 via-[#0F4C3A]/40 to-transparent" />

            {/* Image text */}
            <div className="relative h-full flex flex-col justify-end p-9 md:p-12 text-white">
              <span className="section-eyebrow text-[#E6D5A8]">
                PARTNER WITH THE GOWER
              </span>

              <h3 className="font-serif-luxury text-4xl md:text-5xl mt-4 leading-tight">
                Corporate
                <span className="block italic text-gold-metallic">
                  Sponsorship
                </span>
              </h3>

              <p className="text-white/85 mt-5 max-w-md leading-relaxed">
                Align your brand with one of South Wales' most welcoming
                golf destinations and reach a community of members,
                members' guests and travelling golfers.
              </p>

              <div className="mt-6 h-px w-16 bg-[#CBA153]" />
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-6">
            <div className="bg-[#FAFAFA] border border-[#CBA153]/30 p-8 md:p-12 h-full">

              <p className="section-eyebrow">
                Become a Partner
              </p>

              <h3 className="font-serif-luxury text-3xl md:text-4xl text-[#0F4C3A] mt-3 leading-tight">
                Sponsorship &amp;
                <span className="italic text-gold-metallic">
                  {" "}Commercial Partnerships
                </span>
              </h3>

              <p className="text-sm text-[#4A4A4A] mt-4 mb-8 leading-relaxed">
                Inquiries submitted directly to{" "}
                <span className="text-[#0F4C3A] font-medium">
                  info@gowergolf.com
                </span>
                .
              </p>

              <form
                onSubmit={onSubmit}
                className="space-y-6"
                data-testid="partner-form"
              >

                {/* COMPANY + CONTACT */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <div>
                    <label
                      className="luxury-label"
                      htmlFor="p-company"
                    >
                      Company Name
                    </label>

                    <input
                      id="p-company"
                      data-testid="partner-input-company"
                      className="luxury-input"
                      value={form.company_name}
                      onChange={update("company_name")}
                      required
                    />
                  </div>

                  <div>
                    <label
                      className="luxury-label"
                      htmlFor="p-contact"
                    >
                      Contact Name
                    </label>

                    <input
                      id="p-contact"
                      data-testid="partner-input-contact"
                      className="luxury-input"
                      value={form.contact_name}
                      onChange={update("contact_name")}
                      required
                    />
                  </div>

                </div>

                {/* EMAIL */}
                <div>
                  <label
                    className="luxury-label"
                    htmlFor="p-email"
                  >
                    Email
                  </label>

                  <input
                    id="p-email"
                    data-testid="partner-input-email"
                    type="email"
                    className="luxury-input"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>

                {/* MESSAGE */}
                <div>
                  <label
                    className="luxury-label"
                    htmlFor="p-msg"
                  >
                    Message / Sponsorship Level
                  </label>

                  <textarea
                    id="p-msg"
                    data-testid="partner-input-msg"
                    rows="4"
                    className="luxury-input resize-none"
                    placeholder="Tell us about your brand and the partnership tier you're considering."
                    value={form.message}
                    onChange={update("message")}
                    required
                  />
                </div>

                {/* SUBMIT */}
                <button
                  data-testid="partner-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="ggc-btn-primary disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2
                      className="animate-spin"
                      size={16}
                    />
                  ) : (
                    "Become a Partner"
                  )}
                </button>

              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
